import clsxm from '@/lib/clsxm';

import AgentFaq from '@/components/agents/AgentFaq';
import AgentFinalCta from '@/components/agents/AgentFinalCta';
import AgentReviews from '@/components/agents/AgentReviews';
import { tone } from '@/components/agents/primitives';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import AlternativeHero from './AlternativeHero';
import BetterChoice from './BetterChoice';
import ComparisonTable from './ComparisonTable';
import Differences from './Differences';
import { breadcrumbJsonLd, JsonLd, LAST_CHECKED_ISO, SITE } from './kit';
import MoreComparisons from './MoreComparisons';
import PricingCompare from './PricingCompare';
import SourcesList from './SourcesList';
import SwitchGuide from './SwitchGuide';
import type { AlternativePageProps } from './types';
import VerdictCards from './VerdictCards';

/*
 * One comparison page (English only; other languages redirect here). Story:
 * the answer first (hero card and verdict), then the evidence (table,
 * differences, prices), the honest part (when the other tool wins), how to
 * switch, and the questions people search for.
 */
export default function AlternativePage(props: AlternativePageProps) {
  const { slug, name, seo } = props;
  const path = `/alternatives/${slug}`;

  return (
    <main className={clsxm('relative overflow-x-clip', tone.page)}>
      <Seo
        title={seo.title}
        description={seo.description}
        image={`${SITE}/api/og-template?title=${encodeURIComponent(
          `${name} alternative`,
        )}`}
        structuredData={false}
      />
      <JsonLd
        data={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'AI Agents', path: '/chatbots' },
            { name: 'Comparisons', path: '/alternatives' },
            { name: props.keyword, path },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: seo.title,
            description: seo.description,
            url: `${SITE}${path}`,
            dateModified: LAST_CHECKED_ISO,
            inLanguage: 'en',
            isPartOf: { '@type': 'WebSite', name: 'Easy-Peasy.AI', url: SITE },
          },
        ]}
      />

      <AlternativeHero
        name={name}
        keyword={props.keyword}
        hero={props.hero}
        glance={props.glance}
        category={props.category}
      />
      <VerdictCards name={name} verdict={props.verdict} />
      <ComparisonTable name={name} table={props.table} />
      <Differences differences={props.differences} ours={props.ours} />
      <PricingCompare name={name} pricing={props.pricing} ours={props.ours} />
      <BetterChoice name={name} slug={slug} betterChoice={props.betterChoice} />
      <SwitchGuide name={name} migration={props.migration} />
      <AgentReviews />
      <AgentFaq faqs={props.faqs} />
      <MoreComparisons others={props.others} />
      <SourcesList name={name} sources={props.sources} />
      <AgentFinalCta
        title={
          slug === 'drift'
            ? 'Be live long before Drift switches off.'
            : `Build yours in minutes. Keep ${name} until you’re sure.`
        }
        exchange={props.exchange}
      />
      <Footer />
    </main>
  );
}
